import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../Sidebar/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faPlus, faCheck, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../../config/api.config';

const SalarySlips = ({ showAlert }) => {
  const [staffList, setStaffList] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    staffId: '',
    baseSalary: '',
    bonus: '',
    deductions: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const restaurantId = localStorage.getItem('currentRestaurantId');
      const token = localStorage.getItem('token');

      // Fetch staff
      const staffRes = await axios.post(buildApiUrl("staff/list"), 
        { restaurantId },
        { headers: { 'auth-token': token } }
      );
      setStaffList(staffRes.data);

      // Fetch salaries
      const salaryRes = await axios.post(buildApiUrl("hr/salary/list"),
        { restaurantId, month: selectedMonth, year: selectedYear },
        { headers: { 'auth-token': token } }
      );
      setSalaries(salaryRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSlip = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const restaurantId = localStorage.getItem('currentRestaurantId');
      const token = localStorage.getItem('token');

      await axios.post(buildApiUrl("hr/salary/create"),
        {
          staffId: formData.staffId,
          restaurantId,
          month: selectedMonth,
          year: selectedYear,
          baseSalary: parseFloat(formData.baseSalary),
          bonus: parseFloat(formData.bonus) || 0,
          deductions: parseFloat(formData.deductions) || 0,
          notes: formData.notes
        },
        { headers: { 'auth-token': token } }
      );

      if (showAlert) showAlert("Salary slip created!", "success");
      setShowForm(false);
      setFormData({ staffId: '', baseSalary: '', bonus: '', deductions: '', notes: '' });
      fetchData();
    } catch (error) {
      console.error("Error creating salary slip:", error);
      if (showAlert) showAlert("Failed to create salary slip", "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPaid = async (salaryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(buildApiUrl(`hr/salary/pay/${salaryId}`),
        {},
        { headers: { 'auth-token': token } }
      );
      if (showAlert) showAlert("Marked as paid!", "success");
      fetchData();
    } catch (error) {
      console.error("Error marking as paid:", error);
    }
  };

  const handleDelete = async (salaryId) => {
    if (!window.confirm("Delete this salary slip?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(buildApiUrl(`hr/salary/${salaryId}`),
        { headers: { 'auth-token': token } }
      );
      if (showAlert) showAlert("Salary slip deleted", "success");
      fetchData();
    } catch (error) {
      console.error("Error deleting salary:", error);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0a0a0c', color: 'white' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '800', margin: 0 }}>
            <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: '1rem', color: '#f59e0b' }} />
            Salary Slips
          </h1>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <select 
              value={selectedMonth} 
              onChange={e => setSelectedMonth(parseInt(e.target.value))}
              style={{ padding: '0.8rem', background: '#1c1c1f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
            >
              {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select 
              value={selectedYear} 
              onChange={e => setSelectedYear(parseInt(e.target.value))}
              style={{ padding: '0.8rem', background: '#1c1c1f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
            >
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button 
              onClick={() => setShowForm(!showForm)}
              style={{ padding: '0.8rem 1.5rem', background: '#f59e0b', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
              Create Slip
            </button>
          </div>
        </div>

        {/* Create Salary Form */}
        {showForm && (
          <div style={{ background: '#1c1c1f', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>Create Salary Slip for {months[selectedMonth - 1]} {selectedYear}</h3>
            <form onSubmit={handleCreateSlip} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <select 
                value={formData.staffId} 
                onChange={e => setFormData({...formData, staffId: e.target.value})}
                required
                style={{ padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: 'white' }}
              >
                <option value="">Select Staff</option>
                {staffList.map(s => <option key={s._id} value={s._id}>{s.name} ({s.staffRole})</option>)}
              </select>
              <input 
                type="number" 
                placeholder="Base Salary" 
                value={formData.baseSalary}
                onChange={e => setFormData({...formData, baseSalary: e.target.value})}
                required
                style={{ padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: 'white' }}
              />
              <input 
                type="number" 
                placeholder="Bonus (optional)" 
                value={formData.bonus}
                onChange={e => setFormData({...formData, bonus: e.target.value})}
                style={{ padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: 'white' }}
              />
              <input 
                type="number" 
                placeholder="Deductions (optional)" 
                value={formData.deductions}
                onChange={e => setFormData({...formData, deductions: e.target.value})}
                style={{ padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: 'white' }}
              />
              <input 
                type="text" 
                placeholder="Notes (optional)" 
                value={formData.notes}
                onChange={e => setFormData({...formData, notes: e.target.value})}
                style={{ padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: 'white' }}
              />
              <button 
                type="submit" 
                disabled={submitting}
                style={{ padding: '0.8rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Create'}
              </button>
            </form>
          </div>
        )}

        {/* Salary List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.5)' }}>Loading...</div>
        ) : salaries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.5)' }}>No salary slips for this month.</div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {salaries.map(salary => (
              <div key={salary._id} style={{ 
                background: '#1c1c1f', 
                padding: '1.5rem', 
                borderRadius: '12px',
                border: `1px solid ${salary.status === 'paid' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(255,255,255,0.05)'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {salary.staffId?.profilePhoto ? (
                      <img src={salary.staffId.profilePhoto} alt="" style={{ width: '50px', height: '50px', borderRadius: '10px', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '50px', height: '50px', borderRadius: '10px', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b', fontWeight: 'bold', fontSize: '1.2rem' }}>
                        {salary.staffId?.name?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>{salary.staffId?.name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>{salary.staffId?.staffRole}</div>
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#22c55e' }}>₹{salary.netSalary?.toLocaleString()}</div>
                    <div style={{ 
                      display: 'inline-block',
                      padding: '0.3rem 0.8rem', 
                      borderRadius: '20px', 
                      fontSize: '0.75rem', 
                      fontWeight: '600',
                      background: salary.status === 'paid' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                      color: salary.status === 'paid' ? '#22c55e' : '#f59e0b'
                    }}>
                      {salary.status === 'paid' ? 'PAID' : 'PENDING'}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '1rem', marginTop: '1rem', padding: '1rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>BASE</div>
                    <div style={{ fontWeight: '600' }}>₹{salary.baseSalary?.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>BONUS</div>
                    <div style={{ fontWeight: '600', color: '#22c55e' }}>+₹{salary.bonus?.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>DEDUCTIONS</div>
                    <div style={{ fontWeight: '600', color: '#ef4444' }}>-₹{salary.deductions?.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>DAYS WORKED</div>
                    <div style={{ fontWeight: '600' }}>{salary.presentDays}/{salary.workingDays}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  {salary.status !== 'paid' && (
                    <button 
                      onClick={() => handleMarkPaid(salary._id)}
                      style={{ padding: '0.6rem 1rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}
                    >
                      <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.3rem' }} /> Mark Paid
                    </button>
                  )}
                  <button 
                    onClick={() => handleDelete(salary._id)}
                    style={{ padding: '0.6rem 1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalarySlips;
